import React, { useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import { partial } from 'lodash';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import withProps from '@dyl/utils/withProps';
import TaskItems from '@dyl/blocks/TaskItems';
import ImagePicker from '@dyl/components/ImagePicker';
import AudioAndTranscriptBlock from '@dyl/blocks/AudioAndTranscriptBlock';
import SimpleTaskField from "@dyl/blocks/SimpleTaskField";
import PersonalIntroductionOption from '@dyl/blocks/PersonalIntroductionOption';
import RubricBlock from '@dyl/blocks/RubricBlock';
import PropTypes from 'prop-types';
import { limitWordsAndSpaces } from '../../../utils/limitWordsAndSpaces';

function Item({ index }) {
    const useField = partial(useTaskField, 'items', index);
    const [item, onChange] = useField();
    const isExample = item.is_example;

    return isExample
        ? <ExampleItem index={index} item={item} onChange={onChange} />
        : <RegularItem index={index} item={item} onChange={onChange} />;
}

function ExampleItem({ index, item, onChange }) {
    const useField = partial(useTaskField, 'items', index);
    const [imageId, setImageId] = useState(item.background_image || '');
    const [audioId, setAudioId, audioError] = useField('audio_id');

    useEffect(() => {
        setImageId(item.background_image || '');
    }, [item.background_image]);


    const handleFieldChange = (field, value) => {
        if (onChange) {
            onChange({ ...item, [field]: value });
        }
        if (field === 'background_image') {
            setImageId(value);
        }
    };

    const questionValue = item?.stimulus_text || '';
    const handleQuestionChange = (val) => {
        const restricted_text = limitWordsAndSpaces(val, 10);
        handleFieldChange('stimulus_text', restricted_text);
    };

    return (
        <div style={{ minHeight: '80vh', alignItems: 'center', justifyContent: 'center' }}>
            <div>
                <Typography variant="h6" sx={{ marginBottom: '8px' }}>
                    {index === 0 ? 'Example' : `Item ${index}`}
                </Typography>
                <div style={{ marginBottom: '16px' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
                        Background Image
                    </Typography>
                    <ImagePicker
                        label="Example background image"
                        value={imageId}
                        onChange={id => handleFieldChange('background_image', id)}
                        fullWidth
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <RubricBlock index={index} />
                </div>
                <div style={{ display: 'flex', gap: 0, paddingTop: 0 }}>
                    <AudioAndTranscriptBlock
                        label="Stimulus audio"
                        value={audioId}
                        onChange={id => setAudioId(id)}
                        error={audioError}
                        required
                        sx={{ pt: 0 }}
                    />
                </div>
                <div style={{ marginTop: 16 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 400, marginBottom: '22px', color: 'black', fontFamily: 'Roboto' }}>
                        Maximum 10 words for Question. Maximum 12 words for each Option.
                    </Typography>
                    <Typography sx={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }} noWrap>
                        Question
                    </Typography>
                    <SimpleTaskField
                        path={['items', index, 'stimulus_text']}
                        value={questionValue}
                        onChange={e => handleQuestionChange(e.target.value)}
                    />
                </div>
                <div style={{ marginTop: 16 }}>
                    <PersonalIntroductionOption
                        path={['items', index, 'answers']}
                        fieldName={'key_text'}
                    />
                </div>
            </div>
        </div>
    );
}

ExampleItem.propTypes = {
    index: PropTypes.number.isRequired,
    item: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
};

function RegularItem({ index, item, onChange }) {
    const useField = partial(useTaskField, 'items', index);
    const [imageId, setImageId] = useState(item.background_image || '');
    const [audioId, setAudioId, audioError] = useField('audio_id');


    const handleElementChange = (field, value) => {
        if (onChange) {
            onChange({ ...item, [field]: value });
        }
    };

    const questionValue = item?.stimulus_text || '';
    const handleQuestion = (val) => {
        const restricted_text = limitWordsAndSpaces(val, 10);
        handleElementChange('stimulus_text', restricted_text);
    };


    useEffect(() => {
        setImageId(item.background_image || '');
    }, [item.background_image]);

    return (
        <div style={{ minHeight: '80vh', alignItems: 'center', justifyContent: 'center' }}>
            <div>
                <Typography variant="h6" sx={{ marginBottom: '8px' }}>
                    {`Item ${index}`}
                </Typography>
                <div style={{ marginBottom: '16px' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }} noWrap>
                        Background image
                    </Typography>
                    <ImagePicker
                        label="Background image"
                        value={imageId}
                        onChange={id => {
                            setImageId(id);
                            onChange({ ...item, background_image: id });
                        }}
                        fullWidth
                    />
                </div>
                <div style={{ display: 'flex', gap: 0, paddingTop: 0 }}>
                    <AudioAndTranscriptBlock
                        label="Stimulus audio"
                        value={audioId}
                        onChange={id => setAudioId(id)}
                        error={audioError}
                        required
                        sx={{ pt: 0 }}
                    />
                </div>
                <div style={{ marginTop: 16 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 400, marginBottom: '22px', color: 'black', fontFamily: 'Roboto' }}>
                        Maximum 10 words for Question. Maximum 12 words for each Option.
                    </Typography>
                    <Typography sx={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }} noWrap>
                        Question
                    </Typography>
                    <SimpleTaskField
                        path={['items', index, 'stimulus_text']}
                        value={questionValue}
                        onChange={e => handleQuestion(e.target.value)}
                    />
                </div>
            </div>
            <div style={{ marginTop: 16 }}>
                <PersonalIntroductionOption
                    path={['items', index, 'answers']}
                    fieldName={'key_text'}
                />
            </div>
        </div>
    );
}

RegularItem.propTypes = {
    index: PropTypes.number.isRequired,
    item: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
};

Item.propTypes = {
    index: PropTypes.number.isRequired,
};

export default withProps(TaskItems, { itemComponent: Item });